import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Icon from '../AppIcon';

const API_URL = 'https://api.autocasses.com/api/user/pro-info';

// Fonction pour calculer la distance en km entre deux points GPS
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Rayon de la Terre en km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  return distance;
};

const ServicesSection = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [allServices, setAllServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [showOnlyPro, setShowOnlyPro] = useState(true);
  const [selectedType, setSelectedType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('distance');
  const [displayCount, setDisplayCount] = useState(12); // Multiples de 4 pour le web

  useEffect(() => {
    getCurrentLocation();
    fetchServices();
  }, []);

  useEffect(() => {
    // Appliquer les filtres quand les données changent
    applyFilters();
  }, [allServices, showOnlyPro, selectedType, searchQuery, sortBy]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError(t('services.locationPermission.notSupported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        let errorMessage = t('services.locationPermission.unable');
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = t('services.locationPermission.denied');
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = t('services.locationPermission.positionUnavailable');
            break;
          case error.TIMEOUT:
            errorMessage = t('services.locationPermission.timeout');
            break;
        }
        
        setLocationError(errorMessage);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
    );
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(API_URL, {
        timeout: 10000,
      });
      
      if (response.data.success && response.data.proInfo) {
        // Transformer les données avec calcul de distance
        const services = response.data.proInfo
          .map(transformServiceData);
        
        setAllServices(services);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error loading services:', err);
      
      let errorMessage = t('services.errors.loading');
      
      if (err.response) {
        switch (err.response.status) {
          case 404:
            errorMessage = t('services.errors.notFound');
            break;
          case 500:
            errorMessage = t('services.errors.serverError');
            break;
          default:
            errorMessage = `${t('services.errors.loading')}: ${err.response.status}`;
        }
      } else if (err.request) {
        errorMessage = t('services.errors.connectionError');
      } else {
        errorMessage = `${t('services.errors.loading')}: ${err.message}`;
      }
      
      setError(errorMessage);
      // Charger des données de démonstration en cas d'erreur
      setAllServices(getDemoServices());
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...allServices];
    
    // Filtrer par type de service
    if (selectedType !== 'all') {
      result = result.filter(service => service.type === selectedType);
    }
    
    // Filtrer par statut PRO
    if (showOnlyPro) {
      result = result.filter(service => service.rawData?.isPro == 1);
    }
    
    // Filtrer par recherche
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      result = result.filter(service => 
        service.name.toLowerCase().includes(query) ||
        service.city.toLowerCase().includes(query) ||
        service.category.toLowerCase().includes(query) ||
        service.features.some(feature => feature.toLowerCase().includes(query))
      );
    }
    
    // Trier les résultats
    result.sort((a, b) => {
      switch(sortBy) {
        case 'distance':
          if (a.distance === null && b.distance === null) return 0;
          if (a.distance === null) return 1;
          if (b.distance === null) return -1;
          return a.distance - b.distance;
        case 'rating':
          return b.rating - a.rating;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'reviews':
          return b.reviews - a.reviews;
        default:
          return 0;
      }
    });
    
    setFilteredServices(result);
  };

  const transformServiceData = (service) => {
    // Calculer une note réaliste
    const rating = (3.5 + Math.random() * 1.5).toFixed(1);
    const reviews = Math.floor(Math.random() * 200) + 20;
    
    // Déterminer le type et l'icône
    const serviceType = service.type_pro === 'center' ? 'center' : 'garage';
    const isOpen = Math.random() > 0.2;
    const waitTime = ['20 min', '30 min', '45 min', '1h'][Math.floor(Math.random() * 4)];
    
    // Calculer la distance si on a la position de l'utilisateur et les coordonnées du service
    let distance = null;
    if (userLocation && service.latitude && service.longitude) {
      distance = calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        parseFloat(service.latitude),
        parseFloat(service.longitude)
      );
    }
    
    // Géocodage approximatif si pas de coordonnées mais qu'on a une adresse
    if (!distance && userLocation && service.businessAddress) {
      distance = Math.random() * 20 + 1; // Distance aléatoire entre 1 et 21 km
    }
    
    return {
      id: service.id,
      name: service.nom_pro || service.nom || t('services.unknownAddress'),
      address: service.businessAddress || service.adresse || t('services.unknownAddress'),
      city: extractCity(service.businessAddress || service.adresse),
      rating: parseFloat(rating),
      reviews: reviews,
      type: service.type_pro || 'garage',
      typeLabel: serviceType === 'center' ? t('services.serviceTypes.center') : t('services.serviceTypes.garage'),
      category: service.specialites || (serviceType === 'center' ? t('services.categories.inspection') : t('services.categories.mechanical')),
      image: service.avatar,
      isPro: service.isPro || false,
      open: isOpen,
      telephone: service.tel_pro || service.telephone,
      email: service.businessEmail || service.email,
      description: service.descriptions || `${serviceType === 'center' ? t('services.serviceTypes.center') : t('services.serviceTypes.garage')} ${t('services.features.professional')}`,
      businessHours: service.businessHours || (serviceType === 'center' ? '7h30-17h30' : '8h-18h'),
      website: service.businessWebsite,
      waitTime: serviceType === 'center' ? waitTime : null,
      verified: service.isPro,
      features: getServiceFeatures(serviceType, service.isPro),
      distance: distance,
      latitude: service.latitude,
      longitude: service.longitude,
      rawData: service
    };
  };

  const getDemoServices = () => {
    const demoData = [
      {
        id: 1,
        nom_pro: 'Centre Technique Automobile',
        type_pro: 'center',
        isPro: 1,
        businessAddress: 'Yaoundé, Mfoundi',
        specialites: 'Contrôle technique',
        avatar: null
      },
      {
        id: 2,
        nom_pro: 'Garage Mécanique Générale',
        type_pro: 'garage',
        isPro: 1,
        businessAddress: 'Douala, Bonabéri',
        specialites: 'Mécanique générale',
        avatar: null
      },
      {
        id: 3,
        nom_pro: 'Carrosserie Express',
        type_pro: 'garage',
        isPro: 1,
        businessAddress: 'Yaoundé, Bastos',
        specialites: 'Carrosserie - Peinture',
        avatar: null
      },
      {
        id: 4,
        nom_pro: 'Centre de Diagnostic Électronique',
        type_pro: 'center',
        isPro: 1,
        businessAddress: 'Douala, Akwa',
        specialites: 'Diagnostic électronique',
        avatar: null
      },
      {
        id: 5,
        nom_pro: 'Vulcanisation Pro',
        type_pro: 'garage',
        isPro: 1,
        businessAddress: 'Yaoundé, Nkolbisson',
        specialites: 'Vulcanisation - Pneus',
        avatar: null
      },
      {
        id: 6,
        nom_pro: 'Mécanique Rapide 24/7',
        type_pro: 'garage',
        isPro: 1,
        businessAddress: 'Douala, Deido',
        specialites: 'Dépannage rapide',
        avatar: null
      },
      {
        id: 7,
        nom_pro: 'Centre de Révision Officiel',
        type_pro: 'center',
        isPro: 1,
        businessAddress: 'Yaoundé, Odza',
        specialites: 'Révision complète',
        avatar: null
      },
      {
        id: 8,
        nom_pro: 'Électricité Auto Expert',
        type_pro: 'garage',
        isPro: 1,
        businessAddress: 'Douala, Bonanjo',
        specialites: 'Électricité automobile',
        avatar: null
      }
    ];
    
    return demoData.map(transformServiceData);
  };

  const extractCity = (address) => {
    if (!address) return t('services.unknownCity');
    const parts = address.split(',');
    return parts[parts.length - 1]?.trim() || t('services.unknownCity');
  };

  const getValidImageUrl = (imageUrl) => {
    if (!imageUrl) {
      // Images par défaut différentes pour variété
      const defaultImages = [
        'https://images.unsplash.com/photo-1549399542-7e3f8b79c341?w=800&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1543465077-db45d34b88a5?w=800&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w-800&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1536895058696-a69b1c7ba34f?w=800&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=800&auto=format&fit=crop'
      ];
      return defaultImages[Math.floor(Math.random() * defaultImages.length)];
    }
    
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    if (imageUrl.includes('uploads')) {
      return `https://api.autocasses.com/${imageUrl}`;
    }
    
    return 'https://images.unsplash.com/photo-1549399542-7e3f8b79c341?w=800&auto=format&fit=crop';
  };

  const getServiceFeatures = (type, isPro) => {
    const baseFeatures = isPro ? 
      [t('services.features.professional'), t('services.features.certified')] : 
      [t('services.features.localService')];
    
    if (type === 'center') {
      return [...baseFeatures, t('services.categories.inspection'), t('services.features.maintenance')];
    } else {
      return [...baseFeatures, t('services.features.repair'), t('services.features.maintenance'), t('services.features.parts')];
    }
  };

  const handleServicePress = (service) => {
    navigate('/service-detail', { 
      state: { 
        userId: service.id,
        userName: service.name 
      }
    });
  };

  const handleCallService = (phoneNumber, serviceName, e) => {
    e.stopPropagation();
    if (!phoneNumber) {
      alert(t('services.messages.phoneUnavailable'));
      return;
    }
    
    if (window.confirm(`${t('services.messages.confirmCall')} ${phoneNumber} ?`)) {
      let formattedNumber = phoneNumber.toString().replace(/\s+/g, '');
      
      if (formattedNumber.startsWith('0')) {
        formattedNumber = '+237' + formattedNumber.substring(1);
      } else if (!formattedNumber.startsWith('+')) {
        formattedNumber = '+237' + formattedNumber;
      }
      
      window.location.href = `tel:${formattedNumber}`;
    }
  };

  const handleWhatsAppService = (phoneNumber, serviceName, e) => {
    e.stopPropagation();
    if (!phoneNumber) {
      alert(t('services.messages.whatsappUnavailable'));
      return;
    }
    
    const message = encodeURIComponent(t('services.messages.whatsappMessage', { serviceName }));
    let formattedNumber = phoneNumber.toString().replace(/\s+/g, '');
    
    if (formattedNumber.startsWith('0')) {
      formattedNumber = '+237' + formattedNumber.substring(1);
    } else if (!formattedNumber.startsWith('+')) {
      formattedNumber = '+237' + formattedNumber;
    }
    
    const whatsappUrl = `https://wa.me/${formattedNumber}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 12);
  };

  const displayedServices = filteredServices.slice(0, displayCount);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[600px] bg-gradient-to-b from-slate-50 to-white">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mb-6"></div>
          <p className="text-xl text-slate-700 font-medium mb-2">
            {t('services.messages.loading')}
          </p>
          <p className="text-slate-500">
            Chargement des services professionnels...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-slate-50 to-white py-8 px-4 md:px-6 lg:px-8">
      {/* Header amélioré */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Icon name="Wrench" size={24} color="#3b82f6" />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  {t('services.title')}
                </h2>
                <p className="text-lg text-slate-600 mt-2">
                  {userLocation ? t('services.subtitle') : t('services.subtitleNoLocation')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres et recherche améliorés */}
    

   
     
      </div>

      {/* Liste des services - Grille 4 par ligne */}
      {error && filteredServices.length === 0 ? (
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg border border-red-200 p-12 text-center">
            <div className="bg-red-50 p-6 rounded-full inline-block mb-6">
              <Icon name="AlertTriangle" size={60} color="#ef4444" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              {t('services.errors.loading')}
            </h3>
            <p className="text-slate-600 mb-8 max-w-lg mx-auto">{error}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={fetchServices}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <Icon name="RefreshCw" size={18} color="white" className="inline mr-2" />
                {t('services.buttons.retry')}
              </button>
              <button
                onClick={() => navigate('/')}
                className="border-2 border-slate-300 hover:border-slate-400 text-slate-700 hover:text-slate-800 px-8 py-3 rounded-xl font-semibold transition-all duration-300"
              >
                Retour à l'accueil
              </button>
            </div>
          </div>
        </div>
      ) : displayedServices.length === 0 ? (
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-16 text-center">
            <div className="bg-slate-50 p-8 rounded-full inline-block mb-8">
              <Icon name="Wrench" size={80} color="#cbd5e1" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              {showOnlyPro ? t('services.messages.noProServices') : t('services.messages.noServices')}
            </h3>
            <p className="text-slate-600 mb-8 max-w-lg mx-auto">
              {t('services.messages.noMatch')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedType('all');
                  setShowOnlyPro(false);
                }}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <Icon name="Filter" size={18} color="white" className="inline mr-2" />
                Réinitialiser les filtres
              </button>
              <button
                onClick={fetchServices}
                className="border-2 border-blue-500 text-blue-500 hover:border-blue-600 hover:text-blue-600 px-8 py-3 rounded-xl font-semibold transition-all duration-300"
              >
                <Icon name="RefreshCw" size={18} color="currentColor" className="inline mr-2" />
                {t('services.buttons.refresh')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto">
          {/* Compteur de résultats */}
          <div className="mb-6 flex items-center justify-between">
            <p className="text-slate-700">
              <span className="font-bold text-blue-600">{displayedServices.length}</span> 
              <span className="mx-2">sur</span>
              <span className="font-bold text-slate-900">{filteredServices.length}</span> 
              <span className="ml-2">services trouvés</span>
            </p>
            {userLocation && (
              <div className="flex items-center text-sm text-slate-600">
                <Icon name="MapPin" size={16} color="#64748b" className="mr-2" />
                <span>Services près de vous</span>
              </div>
            )}
          </div>

          {/* Grille des services - 4 par ligne */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {displayedServices.map((service, index) => (
              <div
                key={service.id}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group cursor-pointer"
                onClick={() => handleServicePress(service)}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Header avec image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  
                  {/* Badges superposés */}
                  <div className="absolute top-3 left-3 flex flex-col gap-2">
                    {/* Badge Type */}
                    <div className={`inline-flex items-center px-2.5 py-1.5 rounded-lg text-xs font-bold ${
                      service.type === 'center' 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-blue-500 text-white'
                    }`}>
                      <Icon 
                        name={service.type === 'center' ? 'Building' : 'Wrench'} 
                        size={10} 
                        color="white" 
                        className="mr-1.5" 
                      />
                      <span>{service.typeLabel}</span>
                    </div>
                    
                    {/* Badge PRO */}
                    {service.rawData?.isPro == 1 && (
                      <div className="inline-flex items-center bg-amber-500 text-white px-2.5 py-1.5 rounded-lg text-xs font-bold">
                        <Icon name="Star" size={10} color="white" className="mr-1.5" />
                        <span>PRO</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Statut ouvert/fermé */}
                  <div className={`absolute top-3 right-3 inline-flex items-center px-2.5 py-1.5 rounded-lg text-xs font-bold ${
                    service.open 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-red-500 text-white'
                  }`}>
                    <Icon name="Clock" size={10} color="white" className="mr-1.5" />
                    <span>{service.open ? 'Ouvert' : 'Fermé'}</span>
                  </div>
                  
                  {/* Distance */}
                  {service.distance !== null && (
                    <div className="absolute bottom-3 right-3 inline-flex items-center bg-black/70 backdrop-blur-sm text-white px-2.5 py-1.5 rounded-lg text-xs font-semibold">
                      <Icon name="Navigation" size={10} color="white" className="mr-1.5" />
                      <span>{service.distance.toFixed(1)} km</span>
                    </div>
                  )}
                </div>

                {/* Contenu */}
                <div className="p-5">
                  {/* En-tête avec nom et note */}
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-bold text-slate-900 truncate pr-2 group-hover:text-blue-600 transition-colors">
                      {service.name}
                    </h3>
                    
                    {/* Note */}
                    <div className="flex items-center bg-slate-50 px-2.5 py-1.5 rounded-lg">
                      <Icon name="Star" size={12} color="#fbbf24" />
                      <span className="ml-1.5 text-sm font-bold text-slate-900">{service.rating}</span>
                      <span className="ml-1 text-xs text-slate-500">({service.reviews})</span>
                    </div>
                  </div>
                  
                  {/* Catégorie */}
                  <div className="flex items-center text-slate-600 mb-3">
                    <Icon name="Tag" size={14} color="#64748b" />
                    <span className="ml-2 text-sm truncate">{service.category}</span>
                  </div>
                  
                  {/* Localisation */}
                  <div className="flex items-center text-slate-600 mb-4">
                    <Icon name="MapPin" size={14} color="#64748b" />
                    <span className="ml-2 text-sm truncate">{service.city}</span>
                    {service.distance !== null && (
                      <span className="ml-2 text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                        {service.distance.toFixed(1)} km
                      </span>
                    )}
                  </div>
                  
                  {/* Horaires et temps d'attente */}
                  <div className="flex items-center justify-between mb-5 p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center">
                      <Icon name="Clock" size={14} color="#64748b" />
                      <span className="ml-2 text-sm font-medium text-slate-700">
                        {service.businessHours}
                      </span>
                    </div>
                    {service.waitTime && (
                      <div className="inline-flex items-center bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-semibold">
                        <Icon name="Clock" size={10} color="#3b82f6" />
                        <span className="ml-1">{service.waitTime}</span>
                      </div>
                    )}
                  </div>

                  {/* Boutons d'action */}
                  <div className="flex justify-between items-center">
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => handleCallService(service.telephone, service.name, e)}
                        className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center hover:bg-blue-100 transition-colors shadow-sm hover:shadow"
                        title="Appeler"
                      >
                        <Icon name="Phone" size={18} color="#3b82f6" />
                      </button>
                      
                      <button
                        onClick={(e) => handleWhatsAppService(service.telephone, service.name, e)}
                        className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center hover:bg-emerald-100 transition-colors shadow-sm hover:shadow"
                        title="WhatsApp"
                      >
                        <Icon name="MessageCircle" size={18} color="#25D366" />
                      </button>
                      
                      <button
                        onClick={() => handleServicePress(service)}
                        className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center hover:bg-slate-100 transition-colors shadow-sm hover:shadow"
                        title="Voir détails"
                      >
                        <Icon name="Eye" size={18} color="#64748b" />
                      </button>
                    </div>
                    
                    <button
                      onClick={() => handleServicePress(service)}
                      className="inline-flex items-center bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                    >
                      <span>Détails</span>
                      <Icon name="ArrowRight" size={16} color="white" className="ml-2" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bouton Charger plus */}
          {displayCount < filteredServices.length && (
            <div className="text-center mt-8">
              <button
                onClick={handleLoadMore}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-10 py-3 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center"
              >
                <span>Charger plus ({filteredServices.length - displayCount} restants)</span>
                <Icon name="ChevronDown" size={20} color="white" className="ml-3" />
              </button>
            </div>
          )}
        </div>
      )}

  

      <style jsx>{`
        .grid > div {
          animation: fadeInUp 0.3s ease-out forwards;
          opacity: 0;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ServicesSection;